<template>
    <NoteModal
        :loading="loading"
        :error="error"
        :note-text="note ? note.text : null"
        :added-by="note ? (note.createdBy || 'Unknown') : ''"
        :date="note ? formatDateTime(note.createdAt) : ''"
        @close="$emit('abort')"
    >
        <template #after-note-content>
            <TagSelector
                :rp-name="rpName"
                resource-type="NOTE"
                :value="currentTags"
                @input="onTagsInput"
            />
        </template>
        <template #footer>
            <NewButton
                variant="outline"
                color="neutral"
                @click="$emit('abort')"
            >
                Close
            </NewButton>
            <NewButton
                :disabled="!hasTagChanges || savingTags"
                :loading="savingTags"
                @click="onSaveTags"
            >
                Save Changes
            </NewButton>
        </template>
    </NoteModal>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { EntityNote } from '@evidentid/tprm-portal-lib/models/entity-notes';

    import NewButton from '@/components/new-button/NewButton.vue';
    import { useEntityEventsStore } from '@/modules/entity-events/store';
    import NoteModal from '@/modules/entity-notes/components/NoteModal/NoteModal.vue';
    import TagSelector from '@/modules/entity-tags/components/TagSelector/TagSelector.vue';
    import { useEntityTagsStore } from '@/modules/entity-tags/store';
    import { formatDateTime } from '@/utils/formatDateTime';

    // PRODUCT-26110 QA fixes4: declares every event this procedure component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'abort'/'finish'
    // but it is neither declared in the emits option nor as an 'onAbort'/'onFinish' prop"
    // every time it fires.
    export default defineComponent({
        name: 'ViewEntityNoteProcedure',
        components: { NoteModal, NewButton, TagSelector },
        props: {
            // PRODUCT-26110 QA fixes4: `uuid` is unused; declared purely so Vue 3 doesn't
            // warn "Extraneous non-props attributes (uuid)" (see
            // UploadEntityDocumentProcedure.vue).
            uuid: {
                type: String,
                default: '',
            },
            rpName: {
                type: String as PropType<string>,
                required: true,
            },
            entityId: {
                type: String as PropType<string>,
                required: true,
            },
            noteId: {
                type: String as PropType<string>,
                required: true,
            },
            initialTags: {
                type: Array as PropType<string[]>,
                default: (): string[] => [],
            },
        },
        emits: [ 'abort', 'finish' ],
        data() {
            return {
                loading: true,
                error: '',
                note: null as EntityNote | null,
                savingTags: false,
                currentTags: [] as string[],
            };
        },
        computed: {
            hasTagChanges(): boolean {
                const initial = [ ...this.initialTags ].sort();
                const current = [ ...this.currentTags ].sort();
                if (initial.length !== current.length) {
                    return true;
                }
                return initial.some((tag, index) => tag !== current[index]);
            },
        },
        watch: {
            initialTags: {
                immediate: true,
                handler(tags: string[]) {
                    this.currentTags = [ ...tags ];
                },
            },
        },
        mounted() {
            this.onLoad();
        },
        methods: {
            formatDateTime,
            onTagsInput(tags: string[]): void {
                this.currentTags = tags;
            },
            async onLoad(): Promise<void> {
                try {
                    const gatewayClient = (this as any).$gatewayClient;
                    this.note = await gatewayClient.tprmUi().getEntityNote(
                        this.rpName, this.entityId, this.noteId,
                    );
                } catch (e) {
                    this.error = 'Failed to load note. Please try again.';
                } finally {
                    this.loading = false;
                }
            },
            async onSaveTags(): Promise<void> {
                this.savingTags = true;
                this.error = '';
                try {
                    const gatewayClient = (this as any).$gatewayClient;
                    await gatewayClient.tprmUi().patchResourceTags(
                        this.rpName, this.entityId, {
                            resourceType: 'NOTE',
                            resourceId: this.noteId,
                            tags: this.currentTags,
                        },
                    );
                    useEntityEventsStore().loadActivityHistory({
                        rpName: this.rpName,
                        entityId: this.entityId,
                        filters: {},
                    });
                    useEntityTagsStore().loadTags({ rpName: this.rpName });
                    this.$emit('finish');
                } catch (e) {
                    this.error = 'Failed to save tags. Please try again.';
                } finally {
                    this.savingTags = false;
                }
            },
        },
    });
</script>
